import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";

const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const fullNameFor = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";
const locationFor = (resident = {}) => resident.locationInformation || resident.address || resident.location || {};
const normalizeResident = (resident = {}) => {
  const name = fullNameFor(resident);
  const location = locationFor(resident);
  return {
    id: resident.id || resident.residentId,
    initials: resident.initials || initialsFor(name),
    name,
    preferredName: resident.preferredName || resident.nickname || "",
    gender: resident.gender || resident.sex || "",
    city: location.city || resident.city || "",
    state: location.state || resident.state || "",
    room: resident.room || resident.roomNumber || "",
    ncpStatus: resident.ncpStatus || resident.carePlanStatus || "Current",
    alerts: Number(resident.alerts || resident.openAlerts || resident.openAlertCount || 0),
    status: String(resident.status || "").toUpperCase(),
  };
};

const residentHref = (resident) => {
  const id = resident.id || resident.name;
  return resident.status === "PROSPECTIVE" ? `/residents/${id}/onboarding` : `/residents/${id}`;
};

export default function ResidentsListPage() {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    careService.residents.list()
      .then((data) => {
        if (!active) return;
        setResidents((Array.isArray(data) ? data : []).map(normalizeResident));
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setResidents([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load residents.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const totalAlerts = residents.reduce((sum, resident) => sum + resident.alerts, 0);
  const dueSoon = residents.filter((resident) => resident.ncpStatus !== "Current").length;

  return (
    <CareShell className="dashboard-content residents-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Workspace Residents</h1>
          <p>Residents currently assigned to Sunset Gardens Assisted Living.</p>
        </div>
        <div className="residents-actions">
          <button className="mini-btn"><i className="ti ti-filter" /> Filter</button>
          <Link className="btn primary" to="/residents/new"><i className="ti ti-user-plus" /> Onboard New Resident</Link>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-users", "Total residents", residents.length, "In this workspace"],
          ["ti-heart-handshake", "NCP follow-up", dueSoon, "Need review or update"],
          ["ti-alert-triangle", "Open alerts", totalAlerts, "Across all residents"],
          ["ti-notes", "Daily notes", "24", "Captured this week"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Residents">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="resident-list-toolbar">
          <label className="resident-search">
            <i className="ti ti-search" />
            <input placeholder="Search residents by name, room, contact, or care team" />
          </label>
          <div className="resident-filter-pills">
            <button className="is-active">All</button>
            <button>NCP Due</button>
            <button>Alerts</button>
            <button>Active</button>
          </div>
        </div>

        <div className="resident-directory">
          <div className="resident-directory-row resident-directory-head">
            <span>Resident</span><span>Preferred name</span><span>Gender</span><span>City</span><span>State</span><span>Status</span><span />
          </div>
          {loading ? <p>Loading residents...</p> : null}
          {!loading && !residents.length ? <p>No residents found.</p> : null}
          {residents.map((resident) => (
            <Link className="resident-directory-row" to={residentHref(resident)} key={resident.id || resident.name}>
              <span className="resident-directory-person">
                <b>{resident.initials}</b>
                <span><strong>{resident.name}</strong><small>{resident.room ? `Room ${resident.room}` : "No room assigned"}</small></span>
              </span>
              <span>{resident.preferredName || "Not added"}</span>
              <span>{resident.gender || "Not added"}</span>
              <span>{resident.city || "Not added"}</span>
              <span>{resident.state || "Not added"}</span>
              <span><em className={`pill ${resident.status === "ACTIVE" ? "low" : resident.status === "PROSPECTIVE" ? "blue" : "medium"}`}>{resident.status || "Unknown"}</em></span>
              <span className="resident-row-actions">{resident.alerts ? <em>{resident.alerts}</em> : null}<i className="ti ti-chevron-right" /></span>
            </Link>
          ))}
        </div>
      </Panel>
    </CareShell>
  );
}
